import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ScaleQuality } from '@theory/keys';
import { SCALES, type ScaleId } from '@theory/scales';
import type { Mode } from '@theory/modes';
import { decodeKeyId } from '@util/musicTheory';

const MAX_RECENT_KEYS = 5;

interface AppState {
  selectedKeyId: string;
  selectedQuality: ScaleQuality;
  selectedMode: Mode;
  useTraditionalNames: boolean;
  isLeftHanded: boolean;
  selectedTuningId: string;
  selectedScaleId: ScaleId;
  recentKeyIds: string[];

  selectKey: (keyId: string) => void;
  selectKeyAndMode: (keyId: string) => void;
  setQuality: (quality: ScaleQuality) => void;
  setMode: (mode: Mode) => void;
  setUseTraditionalNames: (val: boolean) => void;
  setLeftHanded: (val: boolean) => void;
  setTuning: (tuningId: string) => void;
  setSelectedScale: (scaleId: ScaleId) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      selectedKeyId: '',
      selectedQuality: 'major',
      selectedMode: 'ionian',
      useTraditionalNames: true,
      isLeftHanded: false,
      selectedTuningId: 'standard',
      selectedScaleId: 'major',
      recentKeyIds: [],

      // Two key-selection actions with deliberately different mode semantics:
      //  • selectKey         — sets the key center but PRESERVES selectedMode.
      //    Used by mode-independent controls (Circle of Fifths + RootDropdown),
      //    where the mode is chosen separately via ModeDropdown, so changing the
      //    root keeps you in the same mode (e.g. D Dorian → E Dorian).
      //  • selectKeyAndMode  — for controls that advertise a CONCRETE major/minor
      //    key (the by-chord picker), where the mode must match the label.
      selectKey: (keyId) =>
        set((state) => {
          const recent = [keyId, ...state.recentKeyIds.filter((id) => id !== keyId)]
            .slice(0, MAX_RECENT_KEYS);
          return { selectedKeyId: keyId, recentKeyIds: recent };
        }),

      selectKeyAndMode: (keyId) =>
        set((state) => {
          const recent = [keyId, ...state.recentKeyIds.filter((id) => id !== keyId)]
            .slice(0, MAX_RECENT_KEYS);
          const decoded = decodeKeyId(keyId);
          const mode: Mode = decoded?.quality === 'minor' ? 'aeolian' : 'ionian';
          return { selectedKeyId: keyId, recentKeyIds: recent, selectedMode: mode };
        }),

      setQuality: (quality) => set({ selectedQuality: quality }),
      setMode: (mode) => set({ selectedMode: mode }),
      setUseTraditionalNames: (val) => set({ useTraditionalNames: val }),
      setLeftHanded: (val) => set({ isLeftHanded: val }),
      setTuning: (tuningId) => set({ selectedTuningId: tuningId }),
      setSelectedScale: (scaleId) => set({ selectedScaleId: scaleId }),
    }),
    {
      name: 'fretnaught-web-v1',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedKeyId: state.selectedKeyId,
        selectedQuality: state.selectedQuality,
        selectedMode: state.selectedMode,
        useTraditionalNames: state.useTraditionalNames,
        isLeftHanded: state.isLeftHanded,
        selectedTuningId: state.selectedTuningId,
        selectedScaleId: state.selectedScaleId,
        recentKeyIds: state.recentKeyIds,
      }),
      // A short-lived previous build exposed arpeggio entries (e.g. `arp-maj`)
      // inside SCALES and saved them as `selectedScaleId`. Arpeggios now live
      // in their own data file, so any persisted id outside the SCALES dict
      // must be rewritten to a safe default.
      migrate: (persisted, _version) => {
        const next = (persisted ?? {}) as Partial<AppState>;
        if (!next.selectedScaleId || !(next.selectedScaleId in SCALES)) {
          next.selectedScaleId = 'major';
        }
        return next as AppState;
      },
    },
  ),
);
